<script setup>
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import axios from '@/plugins/axios';

import { useStorage } from '@/composables/use-storage';
import { useLayoutComponents } from '@/composables/layout.js';
import { useIcons } from '@/composables/icons.js';
import { useUserStore } from '@/store/UserStore';
import { useToast } from '@/composables/use-toast';

const userStore = useUserStore();


// Composables
const router = useRouter();
const { TextInput, Divider, PrimaryButton, ButtonLoading, Toast } = useLayoutComponents();
const {  Lock } = useIcons();
const { addToast } = useToast();

const { saveAuthToken, saveUserInfo, verifyAuthToken, verifyUserInfo, getUserInfo } = useStorage();

// Reactive state
const email = ref('');
const password = ref('');
const errorMessage = ref('');
const loading = ref(false);

// Methods
const login = async () => {
  try {
    if (!checkFieldsFill()) {
      errorMessage.value = 'Please fill in all fields';
      return;
    }
    
    loading.value = true;

    let login = {password: password.value};
    login[isEmaiOrUsername(email.value)] = email.value;

    const response = await axios.post('/api/login', login);

    if (response.data.token) {
      saveAuthToken(response.data.token);
    }
    
    saveUserInfo(response.data.userInfos);  

    const { profile_pic } = getUserInfo();
    if (profile_pic) {
      try {
        const profilePicture = profile_pic;
        localStorage.setItem('profilePicture', profilePicture);
      } catch (picError) {
        console.error('Error processing profile picture:', picError);
      }
    }
      
    if (verifyAuthToken() && verifyUserInfo()) {
      router.push('/explore');
    } else {
      throw new Error('Failed to store authentication data');
    }
    loading.value = false;
  } catch (error) {

    addToast(error.response?.data?.message, 'error');
    console.error('Error logging in:', error);
    errorMessage.value = 'Invalid email or password';
    loading.value = false;
  }
};

const isEmaiOrUsername = (email) => {
  return email.includes('@') ? 'email' : 'username';
};

const checkFieldsFill = () => {
  return email.value && password.value;
}

const forgotPassword = () => {
  if(isEmaiOrUsername(email.value) === 'email') {
    userStore.setEmail(email.value);
  }
  router.push('/forgot-password');
}


</script>

<template>
 
  <Toast />
  <div class="login">
    <div class="login-card">
      <!-- Header -->
      <div class="login-card__header">
        <img src="/favicon.ico" alt="logo" class="login-card__header-logo" />
        <h2 class="login-card__header-title" data-testid="login-form-title">Welcome Back</h2>
        <span class="login-card__header-subtitle">
          Don't have an account yet? 
          <router-link to="/register" class="text-echoes-primary hover:text-echoes-primary/80 font-semibold" data-testid="sign-up-link">
            Sign up
          </router-link>
        </span>
      </div>
      
      <!-- Body -->
      <div class="login-card__body">
        <form @submit.prevent="login" class="login-card__body-form">
          <!-- Email input -->
          <div class="login-card__body-form-group">
            <TextInput v-model="email" type="email" id="email" placeholder="Username or email address" required dataTestId="email-input">
              <template #icon>
                <i class="uil uil-user"></i>
              </template>
            </TextInput>
          </div>
          
          <!-- Password input -->
          <div class="login-card__body-form-group">
            <TextInput v-model="password" type="password" id="password" placeholder="Password" required dataTestId="password-input">
              <template #icon>
                <Lock class="w-5 h-5" />
              </template>
            </TextInput>     
          </div>

          <!-- Login button -->
          <div class="text-center h-10 mt-4">
            <PrimaryButton type="primary" class="login-card__body-form-button" @click="login" :disabled="loading || !checkFieldsFill()" dataTestId="login-button">
              <ButtonLoading v-if="loading" size="sm" text="Logging in..." />
              <template v-else>Log in</template>
            </PrimaryButton>
          </div>
        </form>

        <div class="text-center">
          <router-link to="/forgot-password" class="text-sm text-white/80 hover:text-echoes-primary/80 font-semibold" @click="forgotPassword" data-testid="forgot-password-link">
            Forgot password?
          </router-link>
        </div>


        <div class="flex justify-center w-full">
          <router-link to="/explore" class="text-echoes-primary hover:text-echoes-primary/80 font-semibold flex items-center gap-2" data-testid="go-to-explore-link">
            <i class="uil uil-arrow-left"></i>
            <span>Go to explore</span>
          </router-link>
        </div>

        <!-- Social login section -->
        <Divider text="OR" class="hidden" />
        <div class="social-login-buttons">
          <!-- Apple login -->
          <button class="social-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path fill="#FFFFFF"
                d="M17.537 12.625a4.421 4.421 0 0 0 2.684 4.047 10.96 10.96 0 0 1-1.384 2.845c-.834 1.218-1.7 2.432-3.062 2.457-1.34.025-1.77-.794-3.3-.794-1.531 0-2.01.769-3.275.82-1.316.049-2.317-1.318-3.158-2.532-1.72-2.484-3.032-7.017-1.27-10.077A4.9 4.9 0 0 1 8.91 6.884c1.292-.025 2.51.869 3.3.869.789 0 2.27-1.075 3.828-.917a4.67 4.67 0 0 1 3.66 1.984 4.524 4.524 0 0 0-2.16 3.805m-2.52-7.432A4.4 4.4 0 0 0 16.06 2a4.482 4.482 0 0 0-2.945 1.516 4.185 4.185 0 0 0-1.061 3.093 3.708 3.708 0 0 0 2.967-1.416Z" />
            </svg>
          </button>
          
          <!-- Google login -->
          <button class="social-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path fill="#FFFFFF" fill-rule="evenodd"
                d="M12.037 21.998a10.313 10.313 0 0 1-7.168-3.049 9.888 9.888 0 0 1-2.868-7.118 9.947 9.947 0 0 1 3.064-6.949A10.37 10.37 0 0 1 12.212 2h.176a9.935 9.935 0 0 1 6.614 2.564L16.457 6.88a6.187 6.187 0 0 0-4.131-1.566 6.9 6.9 0 0 0-4.794 1.913 6.618 6.618 0 0 0-2.045 4.657 6.608 6.608 0 0 0 1.882 4.723 6.891 6.891 0 0 0 4.725 2.07h.143c1.41.072 2.8-.354 3.917-1.2a5.77 5.77 0 0 0 2.172-3.41l.043-.117H12.22v-3.41h9.678c.075.617.109 1.238.1 1.859-.099 5.741-4.017 9.6-9.746 9.6l-.215-.002Z"
                clip-rule="evenodd" />
            </svg>
          </button>
          
          <!-- Twitter/X login -->
          <button class="social-button">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24">
              <path fill="#FFFFFF"
                d="M13.795 10.533 20.68 2h-3.073l-5.255 6.517L7.69 2H1l7.806 10.91L1.47 22h3.074l5.705-7.07L15.31 22H22l-8.205-11.467Zm-2.38 2.95L9.97 11.464 4.36 3.627h2.31l4.528 6.317 1.443 2.02 6.018 8.409h-2.31l-4.934-6.89Z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.login {
  @apply flex flex-col justify-center items-center h-[100dvh] w-screen px-4;
  background-image: linear-gradient(to bottom, #232323, #000000);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.login-card {
  @apply w-full max-w-sm rounded-2xl p-4 shadow-lg bg-echoes-background-black border border-echoes-border-gray;
  backdrop-filter: blur(8px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3), 0 4px 6px rgba(0, 0, 0, 0.2);

}

.login-card__header {
  @apply mb-4 flex flex-col items-center justify-center gap-2;
}

.login-card__header-title {
  @apply text-2xl font-semibold text-white;
}

.login-card__header-subtitle {
  @apply text-sm text-white/80;
}

.login-card__header-logo {
  @apply w-12 h-12;
}

.login-card__body {
  @apply space-y-4;
}

.login-card__body-form {
  @apply flex flex-col gap-2;
}

.login-card__body-form-group {
  @apply flex flex-col gap-2;
}

.login-card__body-form-button {
  @apply w-full shadow-lg h-full;
}

.login-card__footer {
  @apply mt-6 flex justify-between;
}

.social-login-buttons {
  @apply hidden justify-center gap-4;
}

.social-button {
  @apply flex items-center justify-center gap-2 w-full p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors shadow-md;
}
</style>